import {
  BrandingConfig,
  PageKey,
  uiBrandingQueryKey,
  useMutationUpdateUiBranding,
  useQueryGetUiBrandingById,
  useQueryGetUiBrandingByOrganization,
} from "@apis:ui_branding/ui_branding";
import { SuperDialog } from "@components:SuperDialog";
import { useInvalidateQueries } from "@hooks:useInvalidateQueries";
import { Box, CircularProgress } from "@mui/material";
import { useToasterStoreActions } from "@stores:global";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { BrowserSection } from "./BrowserSection";
import {
  buildBrandingConfig,
  formValuesFromConfig,
} from "./buildBrandingConfig";
import { DEFAULT_ICON_TYPES, PAGE_KEYS } from "./constants";
import { CoreSection } from "./CoreSection";
import { IconsSection } from "./IconsSection";
import { ResetButton } from "./ResetButton";
import { SectionNav } from "./SectionNav";
import { TextSection } from "./TextSection";
import { FormData, SectionKey, UiBrandingModalProps } from "./types";
import { useFaviconUpload } from "./useFaviconUpload";
import { useLogoUpload } from "./useLogoUpload";

export const UiBrandingModal = ({
  isOpen,
  onClose,
  branding,
}: UiBrandingModalProps) => {
  const { t } = useTranslation();
  const invalidateQueries = useInvalidateQueries();
  const toastActions = useToasterStoreActions();

  const { data: freshBranding, isLoading } = useQueryGetUiBrandingById(
    { id: branding.id },
    { enabled: isOpen },
  );

  // For bots, fetch org-level branding so "Reset" can copy the org values
  const isBotBranding = !!branding.botId;
  const { data: orgBranding } = useQueryGetUiBrandingByOrganization(
    { organizationId: branding.organizationId },
    { enabled: isOpen && isBotBranding },
  );
  const canResetToOrg = isBotBranding && !!orgBranding;

  const [activeSection, setActiveSection] = useState<SectionKey>("core");
  const [activePage, setActivePage] = useState<PageKey>("chat");
  const [iconTypes, setIconTypes] =
    useState<Record<PageKey, "preset" | "custom">>(DEFAULT_ICON_TYPES);

  const { control, handleSubmit, formState, reset, setValue, watch } =
    useForm<FormData>({ defaultValues: formValuesFromConfig(undefined) });

  const { logoPreview, setLogoPreview, handleLogoUpload, handleRemoveLogo } =
    useLogoUpload(setValue);

  const {
    faviconPreview,
    setFaviconPreview,
    handleFaviconUpload,
    handleRemoveFavicon,
  } = useFaviconUpload(setValue);

  const pages = watch("pages");
  const faviconUrl = watch("faviconUrl");

  // Sync form + previews + icon types from a branding config
  const applyConfig = (
    config: BrandingConfig | null | undefined,
    resetOptions?: Parameters<typeof reset>[1],
  ) => {
    reset(formValuesFromConfig(config), resetOptions);
    setLogoPreview(config?.logoBase64 ?? null);
    setFaviconPreview(config?.faviconBase64 ?? null);

    const newIconTypes = { ...DEFAULT_ICON_TYPES };
    PAGE_KEYS.forEach((page) => {
      if (config?.pages?.[page]?.customIconSvg) {
        newIconTypes[page] = "custom";
      }
    });
    setIconTypes(newIconTypes);
  };

  useEffect(() => {
    if (freshBranding) {
      applyConfig(freshBranding.config);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freshBranding]);

  const updateMutation = useMutationUpdateUiBranding({
    onSuccess: async () => {
      toastActions.setToaster({
        open: true,
        message: t("preferences.uiBranding.updated"),
        variant: "success",
      });
      await invalidateQueries.run([
        { queryKey: uiBrandingQueryKey.all(true).queryKey },
        { queryKey: uiBrandingQueryKey.all(false).queryKey },
        { queryKey: uiBrandingQueryKey.byId(branding.id).queryKey },
        {
          queryKey: uiBrandingQueryKey.byOrganization(branding.organizationId)
            .queryKey,
        },
        ...(branding.botId
          ? [{ queryKey: uiBrandingQueryKey.byBot(branding.botId).queryKey }]
          : []),
      ]);
      onClose();
    },
    onError: () => {
      toastActions.setToaster({
        open: true,
        message: t("preferences.uiBranding.updateFailed"),
        variant: "error",
      });
    },
  });

  // Reset to Nemo defaults
  const handleReset = () => {
    applyConfig(undefined, { keepDefaultValues: true });
  };

  // Reset to the org-level branding (for bots only)
  const handleResetToOrg = () => {
    if (!orgBranding) return;
    applyConfig(orgBranding.config, { keepDefaultValues: true });
  };

  const handleIconTypeChange = (type: "preset" | "custom") => {
    setIconTypes((prev) => ({ ...prev, [activePage]: type }));
    if (type === "preset") {
      setValue(`pages.${activePage}.customIconSvg`, null, {
        shouldDirty: true,
      });
    } else {
      setValue(`pages.${activePage}.iconKey`, null, { shouldDirty: true });
    }
  };

  const onSubmit = async (data: FormData) => {
    await updateMutation.mutateAsync({
      id: branding.id,
      organizationId: branding.organizationId,
      botId: branding.botId,
      config: buildBrandingConfig(data),
    });
  };

  return (
    <SuperDialog
      open={isOpen}
      onClose={onClose}
      title={t("preferences.uiBranding.editTitle")}
      sx={{
        "& .MuiDialog-paper": {
          width: "40rem",
          maxWidth: "95vw",
          borderRadius: 3,
          maxHeight: "none",
          overflow: "hidden",
        },
      }}
      leftAction={
        isLoading ? undefined : (
          <ResetButton
            onClick={canResetToOrg ? handleResetToOrg : handleReset}
            label={
              canResetToOrg
                ? t("preferences.uiBranding.resetToOrganization")
                : t("preferences.uiBranding.resetToDefault")
            }
          />
        )
      }
      actionControls={{
        secondary: { label: t("common.cancel"), onClick: onClose },
        primary: {
          label: updateMutation.isPending
            ? t("preferences.uiBranding.saving")
            : t("preferences.uiBranding.saveChanges"),
          onClick: handleSubmit(onSubmit),
          disabled: isLoading || updateMutation.isPending || !formState.isDirty,
        },
      }}
    >
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 6,
          }}
        >
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box>
          <SectionNav
            activeSection={activeSection}
            onSectionChange={setActiveSection}
          />
          <Box>
            {activeSection === "core" && (
              <CoreSection
                organizationName={branding.organizationName}
                botName={branding.botName}
                logoPreview={logoPreview}
                onLogoUpload={handleLogoUpload}
                onLogoRemove={handleRemoveLogo}
                control={control}
              />
            )}
            {activeSection === "browser" && (
              <BrowserSection
                faviconPreview={faviconPreview}
                onFaviconUpload={handleFaviconUpload}
                onFaviconRemove={handleRemoveFavicon}
                faviconUrl={faviconUrl}
                setValue={setValue}
                control={control}
              />
            )}
            {activeSection === "icons" && (
              <IconsSection
                activePage={activePage}
                onPageChange={setActivePage}
                iconType={iconTypes[activePage]}
                onIconTypeChange={handleIconTypeChange}
                pages={pages}
                control={control}
                setValue={setValue}
              />
            )}
            {activeSection === "text" && (
              <TextSection
                activePage={activePage}
                onPageChange={setActivePage}
                control={control}
              />
            )}
          </Box>
        </Box>
      )}
    </SuperDialog>
  );
};
